import { type JSX } from "react";
import {
  type ControllerRenderProps,
  type UseFormReturn,
} from "react-hook-form";

import { ReminderType } from "../maintenance.enums";
import { maintenanceMessages } from "../maintenance.intl";
import { type MaintenanceScheduleDraft } from "../types/maintenance.types";
import {
  getMaintenanceDurationLabel,
  getMaintenanceLeadTimeLabel,
} from "../utils/maintenanceTime";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import DateInput from "@/components/customFormFields/DateInput";
import RadioGroupField from "@/components/customFormFields/RadioGroup";
import FormInput from "@/components/formFields/FormInput";
import { TimePicker12 } from "@/components/timePicker/TimePicker12Hour";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils/core/generalUtils";

const MAX_NOTICE_LENGTH = 300;

const getDateValue = (date: string | Date | undefined): Date | undefined => {
  if (!date) {
    return undefined;
  }

  const parsedDate = typeof date === "string" ? new Date(date) : date;

  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

const getPeriod = (
  date: string | Date | undefined,
): "AM" | "PM" | undefined => {
  if (!date) {
    return undefined;
  }
  if (typeof date === "string") {
    return date.includes("PM") ? "PM" : "AM";
  }
  return date.getHours() >= 12 ? "PM" : "AM";
};

interface MaintenanceFormProps {
  form: UseFormReturn<MaintenanceScheduleDraft>;
  onReview: (draft: MaintenanceScheduleDraft) => void;
  onCancel?: () => void;
  hideActions?: boolean;
  formId?: string;
}

/**
 * Renders the maintenance schedule form.
 *
 * @param props Component props.
 * @returns Maintenance form element.
 */
const MaintenanceForm = (props: MaintenanceFormProps): JSX.Element => {
  const { form, onCancel, onReview, hideActions = false, formId } = props;
  const t = useIntlMessages(maintenanceMessages);
  const draft = form.watch();

  const durationLabel = getMaintenanceDurationLabel(draft);
  const leadTimeLabel = getMaintenanceLeadTimeLabel(draft);

  const handleReview = form.handleSubmit((scheduleDraft) => {
    onReview(scheduleDraft);
  });

  return (
    <form
      id={formId}
      className='w-full max-w-7xl rounded-lg bg-white pt-4'
      onSubmit={handleReview}
    >
      <div>
        <h2 className='text-xl font-semibold text-slate-950'>
          {t("schedule")}
        </h2>
        <p className='mt-2 text-sm text-slate-500'>{t("pageDescription")}</p>
      </div>

      <div className='mt-6 grid gap-6 xl:grid-cols-2 items-start'>
        <div className='grid gap-3 sm:grid-cols-2 sm:items-end'>
          <FormInput
            name='startDate'
            form={form}
            label={t("startsOn")}
            required
            className='min-w-0'
            render={(field) => (
              <DateInput
                value={field.value}
                onChange={field.onChange}
                placeholder={t("selectDate")}
                className={cn("!w-full")}
              />
            )}
          />
          <FormInput
            name='startTime'
            form={form}
            render={(field) => (
              <TimePicker12
                date={getDateValue(field.value)}
                setDate={field.onChange}
                AMPM={getPeriod(field.value)}
                showIcon
                showResetButton
                className={cn("w-fit opacity-100")}
              />
            )}
          />
        </div>

        <div className='grid gap-3 sm:grid-cols-2 sm:items-end'>
          <FormInput
            name='endDate'
            form={form}
            label={t("endsOn")}
            className='min-w-0'
            render={(field) => (
              <DateInput
                value={field.value}
                onChange={field.onChange}
                placeholder={t("selectDate")}
                className={cn("!w-full")}
              />
            )}
          />
          <FormInput
            name='endTime'
            form={form}
            render={(field) => (
              <TimePicker12
                date={getDateValue(field.value)}
                setDate={field.onChange}
                AMPM={getPeriod(field.value)}
                showIcon
                showResetButton
                className={cn("w-fit opacity-100")}
              />
            )}
          />
        </div>
        {durationLabel && (
          <p className='inline-flex w-fit rounded-md bg-orange-50 px-3 py-1 text-sm text-orange-700'>
            {t("duration")}: {durationLabel}
          </p>
        )}
      </div>

      <div className='mt-10'>
        <h3 className='text-lg font-semibold text-slate-950'>
          {t("reminder")}
        </h3>
        <p className='mt-2 text-sm text-slate-500'>
          {t("reminderDescription")}
        </p>
        <FormInput
          name='reminderType'
          form={form}
          render={(field) => (
            <RadioGroupField
              field={field}
              className='mt-6'
              options={[
                { label: t("customDate"), value: "CUSTOM" },
                { label: t("doNotNotify"), value: "NONE" },
                { label: t("notifyImmediately"), value: "IMMEDIATE" },
              ]}
              defaultValue='true'
            />
          )}
        />

        {draft.reminderType === ReminderType.CUSTOM && (
          <div className='mt-5 grid max-w-xl grid-cols-2 gap-3 items-start'>
            <FormInput
              name='reminderDate'
              form={form}
              render={(field) => (
                <DateInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("selectDate")}
                  className={cn("!w-full")}
                  matchers={
                    draft.startDate
                      ? { after: getDateValue(draft.startDate) ?? new Date() }
                      : undefined
                  }
                />
              )}
            />
            <FormInput
              name='reminderTime'
              form={form}
              render={(field) => (
                <TimePicker12
                  date={getDateValue(field.value)}
                  setDate={field.onChange}
                  AMPM={getPeriod(field.value)}
                  showIcon
                  showResetButton
                  className={cn("w-fit min-w-0")}
                />
              )}
            />
            {leadTimeLabel && (
              <p className='mt-3 inline-flex rounded-md bg-orange-50 px-3 py-1 text-sm text-orange-700 w-fit'>
                {t("leadTime")}: {leadTimeLabel}
              </p>
            )}
          </div>
        )}
      </div>

      <div className='mt-10'>
        <h3 className='text-lg font-semibold text-slate-950'>{t("notice")}</h3>
        <p className='mt-2 text-sm text-slate-500'>{t("noticeDescription")}</p>
        <FormInput
          name='notice'
          form={form}
          className='mt-5'
          render={(
            field: ControllerRenderProps<MaintenanceScheduleDraft, "notice">,
          ) => (
            <Textarea
              className='min-h-24 resize-none'
              maxLength={MAX_NOTICE_LENGTH}
              placeholder={t("writeMessage")}
              {...field}
            />
          )}
        />
        <p className='mt-2 text-right text-xs text-slate-400'>
          {draft.notice?.length ?? 0} / {MAX_NOTICE_LENGTH}
        </p>
      </div>

      {!hideActions && (
        <div className='mt-8 flex justify-end gap-4'>
          <Button
            variant='ghost'
            onClick={() => {
              onCancel?.();
            }}
          >
            {t("reset")}
          </Button>
          <Button type='submit'>{t("review")}</Button>
        </div>
      )}
    </form>
  );
};

export default MaintenanceForm;
